import type React from "react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import CustomSnackbar from "./../components/CustomSnackbar";
import { SNACKBAR_STACK } from "../theme/tokens";

/** Per-snackbar options (all optional/additive — omit to keep the previous behavior). */
type SnackbarOptions = { disableClickawayClose?: boolean };

interface SnackbarContextType {
	showSnackbar: (
		message: string,
		severity: "success" | "info" | "warning" | "error",
		autoHideDuration?: number,
		action?: ReactNode,
		options?: SnackbarOptions,
	) => void;
}

type SnackbarItem = {
	id: number;
	open: boolean;
	message: string;
	severity: "success" | "info" | "warning" | "error";
	autoHideDuration: number;
	action?: ReactNode;
	disableClickawayClose?: boolean;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

/**
 * Chrome pinned to the bottom of the screen that toasts must clear, keyed by whoever registered it.
 *
 * Module-level rather than context state, because its registrants (the gaps-first bottom bar, a gaps sheet while it
 * is up) are mounted far below this provider and must not have to reach it through context. The provider subscribes to
 * it and re-renders when the inset changes — every sheet open, close and resize — so its context value is memoised:
 * that re-render reaches the toasts, never the consumers of `useSnackbar` (the auth and hospital-data providers among
 * them). A Map, not a single value, so StrictMode's double mount — and any second registrant — cannot leave a stale
 * inset behind when one of them unmounts.
 */
const bottomInsets = new Map<symbol, string>();
const bottomInsetSubscribers = new Set<() => void>();
let currentBottomInset: string | undefined;

const publishBottomInset = (): void => {
	currentBottomInset = [...bottomInsets.values()].at(-1);
	for (const onChange of bottomInsetSubscribers) onChange();
};

const subscribeToBottomInset = (onChange: () => void): (() => void) => {
	bottomInsetSubscribers.add(onChange);
	return () => {
		bottomInsetSubscribers.delete(onChange);
	};
};

const getBottomInset = (): string | undefined => currentBottomInset;

/**
 * Lifts every toast above chrome pinned to the bottom of the screen for as long as the caller is mounted — a phone
 * bottom bar, which a toast at the viewport edge would otherwise sit under. Unmounting drops the lift, so everywhere
 * else the app's toasts stay exactly where they were. `null` registers nothing (a sheet that is closed).
 *
 * The most recent registration wins, so a surface opened over the bar (a sheet stopping above it) can lift toasts
 * above ITSELF while it is up, and they fall back to the bar's height when it closes.
 */
export const useSnackbarBottomInset = (inset: string | null): void => {
	useEffect(() => {
		if (inset === null) return;
		const key = Symbol("snackbar-bottom-inset");
		bottomInsets.set(key, inset);
		publishBottomInset();
		return () => {
			bottomInsets.delete(key);
			publishBottomInset();
		};
	}, [inset]);
};

export const useSnackbar = (): SnackbarContextType => {
	const context = useContext(SnackbarContext);
	if (!context) {
		throw new Error("useSnackbar must be used within a SnackbarProvider");
	}
	return context;
};

interface SnackbarProviderProps {
	children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
	const [snackbars, setSnackbars] = useState<SnackbarItem[]>([]);
	const idRef = useRef(0);
	const bottomInset = useSyncExternalStore(subscribeToBottomInset, getBottomInset);

	const showSnackbar = useCallback(
		(message: string, severity: "success" | "info" | "warning" | "error", autoHideDuration?: number, action?: ReactNode, options?: SnackbarOptions) => {
			const id = ++idRef.current;
			const item: SnackbarItem = {
				id,
				open: true,
				message,
				severity,
				autoHideDuration: autoHideDuration ?? 6000,
				action,
				disableClickawayClose: options?.disableClickawayClose,
			};
			setSnackbars((prev) => [...prev, item]);
		},
		[],
	);

	const handleClose = useCallback((id: number) => {
		setSnackbars((prev) => prev.map((s) => (s.id === id ? { ...s, open: false } : s)));
		// Optional tiny cleanup: remove after transition ends (kept minimal & safe to omit)
		setTimeout(() => {
			setSnackbars((prev) => prev.filter((s) => !(s.id === id && !s.open)));
		}, 250);
	}, []);

	// Stable for the provider's life (`showSnackbar` is): a toast shown or an inset changed re-renders the toasts only.
	const contextValue = useMemo<SnackbarContextType>(() => ({ showSnackbar }), [showSnackbar]);

	return (
		<SnackbarContext.Provider value={contextValue}>
			{children}
			{snackbars.map((s, idx) => {
				// number of OPEN snackbars before this one
				const offsetIndex = snackbars.slice(0, idx).filter((x) => x.open).length;
				return (
					<CustomSnackbar
						key={s.id}
						open={s.open}
						handleClose={() => handleClose(s.id)}
						message={s.message}
						severity={s.severity}
						autoHideDuration={s.autoHideDuration}
						offset={offsetIndex * SNACKBAR_STACK.STACK_STEP_PX}
						action={s.action}
						disableClickawayClose={s.disableClickawayClose}
						bottomInset={bottomInset}
					/>
				);
			})}
		</SnackbarContext.Provider>
	);
};
